import { Bodies, RevoluteJoint, GearJoint, World, type Body } from "fast-2d";
import { Camera } from "../render/Camera";
import type { Demo, Scene } from "./types";

const TAU = Math.PI * 2;
const TOOTH_DEPTH = 0.12;

interface Gear {
	body: Body;
	radius: number;
	teeth: number;
	phase: number; // drawing offset so neighbours interlock
	color: string;
}

/**
 * A gear train built from `GearJoint`s: three meshed cogs of different sizes,
 * each pinned to a static axle, all driven by a single motor on the first gear.
 * The joints lock their angular speeds to the radius ratios, so the small gear
 * races while the big one crawls — exactly like real meshing gears. Grab any
 * cog to fight the motor. (Coupled gear trains are a capability Matter.js lacks.)
 */
export const gears: Demo = {
	id: "gears",
	title: "Gears",
	blurb:
		"A meshing gear train made of GearJoints: one motor drives three cogs locked at their radius ratios — the little gear spins fast, the big one slow. Grab a gear to load the train.",
	category: "Joints & Machines",
	build(): Scene {
		const world = new World({ gravity: [0, 0] });

		// Three cogs in a row, each touching the next at the pitch line.
		const radii = [1.6, 1.0, 0.6];
		const teeth = [16, 10, 6];
		const colors = ["#5b8cff", "#46c8a0", "#ffb259"];
		const centers: number[] = [-2.4];
		for (let i = 1; i < radii.length; i++) {
			centers.push(centers[i - 1]! + radii[i - 1]! + radii[i]!);
		}

		const phases = teeth.map((count, index) => {
			const step = TAU / count;
			return index === 1 ? -0.75 * step : -0.25 * step;
		});

		const gearList: Gear[] = [];
		const pins: RevoluteJoint[] = [];
		for (let i = 0; i < radii.length; i++) {
			const r = radii[i]!;
			const cx = centers[i]!;
			// Static axle at the cog's centre; the cog spins freely around it.
			const axle = world.add(
				Bodies.circle({ position: [cx, 0], radius: 0.08, isStatic: true }),
			);
			// Cogs never collide with each other — they are coupled by the joint.
			// The body radius reaches the tooth tips so pointer picking matches the art.
			const body = world.add(
				Bodies.circle({
					position: [cx, 0],
					radius: r + TOOTH_DEPTH,
					density: 1,
					filter: { group: -1 },
				}),
			);
			const pin = new RevoluteJoint(axle, body);
			world.addConstraint(pin);
			pins.push(pin);
			gearList.push({ body, radius: r, teeth: teeth[i]!, phase: phases[i]!, color: colors[i]! });
		}

		// Drive the first gear; everything else follows through the gear coupling.
		pins[0]!.enableMotor = true;
		pins[0]!.motorSpeed = 1.6;
		pins[0]!.maxMotorTorque = 80;

		// Couple each neighbour pair. For external gears, ratio = rNext / rThis gives
		// wThis + ratio * wNext = 0, so the next gear counter-rotates at surface speed.
		for (let i = 1; i < pins.length; i++) {
			world.addConstraint(
				new GearJoint(pins[i - 1]!, pins[i]!, {
					ratio: radii[i]! / radii[i - 1]!,
				}),
			);
		}

		// Keep the train awake even when speeds settle to a steady spin.
		const onBeforeStep = (): void => {
			for (const g of gearList) g.body.wake();
		};

		const drawGear = (
			ctx: CanvasRenderingContext2D,
			cam: Camera,
			g: Gear,
		): void => {
			const cx = cam.worldToScreenX(g.body.position.x);
			const cy = cam.worldToScreenY(g.body.position.y);
			const a = g.body.angle + g.phase;
			const pitch = g.radius * cam.scale;
			const tip = pitch + TOOTH_DEPTH * cam.scale;
			const root = pitch - TOOTH_DEPTH * cam.scale;
			const step = TAU / g.teeth;

			// Cog outline with flat-topped (trapezoidal) teeth so it reads as a
			// gear, not a star: each tooth rises to a tip land, then drops to a
			// root land that forms the gap before the next tooth.
			ctx.beginPath();
			for (let k = 0; k < g.teeth; k++) {
				const base = a + k * step;
				const pts: [number, number][] = [
					[base + step * 0.08, tip],
					[base + step * 0.42, tip],
					[base + step * 0.5, root],
					[base + step * 1.0, root],
				];
				for (const [ang, rad] of pts) {
					const px = cx + Math.cos(ang) * rad;
					const py = cy + Math.sin(ang) * rad;
					if (k === 0 && ang === pts[0]![0]) ctx.moveTo(px, py);
					else ctx.lineTo(px, py);
				}
			}
			ctx.closePath();
			ctx.fillStyle = g.color;
			ctx.fill();

			// Hub + spoke so the rotation reads clearly.
			ctx.fillStyle = "rgba(12,16,28,0.85)";
			ctx.beginPath();
			ctx.arc(cx, cy, root * 0.42, 0, TAU);
			ctx.fill();
			ctx.strokeStyle = "rgba(255,255,255,0.6)";
			ctx.lineWidth = Math.max(2, pitch * 0.06);
			ctx.beginPath();
			ctx.moveTo(cx, cy);
			ctx.lineTo(cx + Math.cos(a) * root * 0.42, cy + Math.sin(a) * root * 0.42);
			ctx.stroke();
			// Centre axle.
			ctx.fillStyle = "rgba(255,255,255,0.85)";
			ctx.beginPath();
			ctx.arc(cx, cy, Math.max(2, pitch * 0.05), 0, TAU);
			ctx.fill();
		};

		const draw = (ctx: CanvasRenderingContext2D, cam: Camera): void => {
			for (const g of gearList) drawGear(ctx, cam, g);
		};

		return {
			world,
			camera: new Camera(centers[1]!, 0, 78),
			onBeforeStep,
			draw,
			hiddenBodies: new Set(gearList.map((g) => g.body)),
			hint: "One motor spins the whole train — the small gear races, the big one crawls. Grab a gear to load it.",
		};
	},
};
